import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'utils/requests';

import type {
  ActivityLogResponse,
  FeatureFlagFilters,
  FeatureFlagsResponse,
  FilterOptions
} from './types';

// Region and auth headers are added by the utils/requests helpers.
const FLAGS_URL = '/customer/platform/feature-flags/all';
const LOGS_URL = '/customer/platform/feature-flags/logs';

// Owner options, from this page's own endpoint.
//
// It used to reuse the global Rollout page's /product/fflag/list/filters,
// which reads a cache keyed by region AND session that only that page writes.
// Arriving from the Platform tab it answered 409 and the dropdown came up
// empty; when it was warm the options were scoped to the global page's last
// filter, so owners visible in this very table were missing (P81-145330).
const FILTERS_URL = '/customer/platform/feature-flags/filters';

// The existing global write paths; they already take an explicit tenant list.
const ASSIGN_URL = '/product/fflag/assign';
const UNASSIGN_URL = '/product/fflag/unassign';

/** The one derived state the GA toggle can remove from the result set. */
export const GA_STATUS = 'globalAssigned';

/**
 * Status here is the DERIVED per-tenant state, not FeatureFlags.status, so the
 * options are a fixed set rather than something the server enumerates.
 * "Assigned" means assigned to THIS tenant — a flag assigned to someone else
 * is "New", exactly as the Status column renders it.
 */
export const STATUS_OPTIONS = [
  { value: 'assigned', label: 'Assigned' },
  { value: GA_STATUS, label: 'General Availability' },
  { value: 'readyToAssign', label: 'Ready to Assign' },
  { value: 'other', label: 'New' }
];

export const fetchFeatureFlags = (
  customerId: string,
  filters: FeatureFlagFilters
): Promise<FeatureFlagsResponse> =>
  postRequest(
    `${FLAGS_URL}?page=${filters.currentPage}&limit=${filters.itemsPerPage}`,
    {
      customerId,
      search: filters.search,
      // Multi select: an array of derived per-tenant state keys.
      status: filters.status,
      ownerName: filters.owner,
      sort: filters.sort,
      includeGlobalAssigned: filters.includeGlobalAssigned
    }
  ).then(res => res.body);

export const fetchActivityLog = (
  customerId: string,
  search: string,
  page: number,
  limit: number
): Promise<ActivityLogResponse> =>
  postRequest(`${LOGS_URL}?page=${page}&limit=${limit}`, {
    customerId,
    search
  }).then(res => res.body);

export const fetchFilterOptions = (): Promise<FilterOptions> =>
  getRequest(FILTERS_URL)
    .then(res => ({
      status: STATUS_OPTIONS,
      owner: res.body?.ownerName || []
    }))
    // The Status options are local, so they must survive the owner call
    // failing — otherwise one dead endpoint empties both dropdowns. This is
    // no longer expected to fire in normal use: the 409-on-cold-cache path it
    // was written for is gone with the endpoint swap above.
    .catch(() => ({ status: STATUS_OPTIONS, owner: [] }));

/**
 * One PUT per flag, issued SEQUENTIALLY. The server's queue-promise only
 * serialises chunks within a single call, so firing these in parallel can
 * interleave read-modify-write on the same document.
 *
 * The counts are tallied here rather than read off a response: the global page
 * sends one request and gets succeeded/failed back, but this page sends N.
 */
/** The server's reason, so a 409 from the GA guard reaches the user. */
export const errorReason = (err: any): string =>
  err?.response?.data?.message || err?.message || 'Unknown error';

export const assignFlags = async (
  featureIds: string[],
  tenantId: string
): Promise<{ succeeded: number; failed: number; reasons: string[] }> => {
  let succeeded = 0;
  const reasons = new Set<string>();

  for (let i = 0; i < featureIds.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await putRequest(ASSIGN_URL, {
        // Always the explicit array: the featureId-only form means "every
        // tenant matching the cached filter" and 409s without the session cache.
        featureId: featureIds[i],
        customerIds: [tenantId]
      });
      succeeded += 1;
    } catch (err) {
      // Collected rather than swallowed: the GA guard answers 409 with the
      // reason, and discarding it left the user with a bare failure count.
      reasons.add(errorReason(err));
    }
  }

  return {
    succeeded,
    failed: featureIds.length - succeeded,
    // Array.from, not a spread: craco build compiles without
    // downlevelIteration, so spreading a Set is a TS2569 compile error even
    // though `tsc --noEmit` against tsconfig.json accepts it.
    reasons: Array.from(reasons)
  };
};

export const unassignFlag = (featureId: string, tenantId: string) =>
  deleteRequest(UNASSIGN_URL, { featureId, customerIds: [tenantId] });
